import { useState, useRef, useCallback } from 'react';
import { Box, Text } from '@chakra-ui/react';
import { formatPeriodLabel } from '../../utils/intlFormatters';

/**
 * The one time-series chart.
 *
 * Every trend line in the app renders through this: the accounts overview, a
 * single account's balance, the category timeline, the dashboard cards and the
 * per-row sparklines. They differ by props, not by implementation, so hover
 * behaviour and styling can never drift apart between views again.
 *
 * Colours are CSS variables off the theme rather than literals, so a palette
 * change — or dark mode — reaches every chart without touching this file.
 *
 * `onHover` is what lets a card's headline figures follow the cursor: the
 * chart owns the pointer maths and reports an index; the caller decides what
 * to say about it.
 */

const VB_W = 380;   // viewBox units; the chart scales to its container
const VB_H = 100;


/**
 * Monotone cubic interpolation (Fritsch–Carlson).
 *
 * Straight segments between monthly points read as a jagged polyline; a plain
 * spline fixes that but overshoots, drawing a balance dipping below a value it
 * never held. Monotone tangents keep the curve inside the data: it is smooth
 * where the data is smooth and flat at every turning point.
 */
function smoothPath(pts) {
  const n = pts.length;
  if (n < 2) return '';
  if (n === 2) return `M${pts[0].x},${pts[0].y} L${pts[1].x},${pts[1].y}`;

  const dx = [], dy = [], slope = [];
  for (let i = 0; i < n - 1; i++) {
    dx[i] = pts[i + 1].x - pts[i].x;
    dy[i] = pts[i + 1].y - pts[i].y;
    slope[i] = dx[i] ? dy[i] / dx[i] : 0;
  }

  const m = [slope[0]];
  for (let i = 1; i < n - 1; i++) {
    if (slope[i - 1] * slope[i] <= 0) m[i] = 0;                 // turning point
    else m[i] = (slope[i - 1] + slope[i]) / 2;
  }
  m[n - 1] = slope[n - 2];

  for (let i = 0; i < n - 1; i++) {
    if (slope[i] === 0) { m[i] = 0; m[i + 1] = 0; continue; }
    const a = m[i] / slope[i];
    const b = m[i + 1] / slope[i];
    const h = a * a + b * b;
    if (h > 9) {                                                 // clamp overshoot
      const t = 3 / Math.sqrt(h);
      m[i] = t * a * slope[i];
      m[i + 1] = t * b * slope[i];
    }
  }

  let d = `M${pts[0].x.toFixed(1)},${pts[0].y.toFixed(1)}`;
  for (let i = 0; i < n - 1; i++) {
    const t = dx[i] / 3;
    d += ` C${(pts[i].x + t).toFixed(1)},${(pts[i].y + m[i] * t).toFixed(1)}`
       + ` ${(pts[i + 1].x - t).toFixed(1)},${(pts[i + 1].y - m[i + 1] * t).toFixed(1)}`
       + ` ${pts[i + 1].x.toFixed(1)},${pts[i + 1].y.toFixed(1)}`;
  }
  return d;
}

export const chartColor = (token) => `var(--chakra-colors-${token.replace(/\./g, '-')})`;

/**
 * A point's ring, as Copilot draws it: 11px across, a 3px stroke in the
 * line's colour around a centre tinted by it. It is HTML over the chart, not
 * SVG inside it: the SVG stretches to its box, which drew every circle as an
 * oval. `x` and `y` are viewBox units.
 */
function PointRing({ x, y, color, glow = false }) {
  const at = {
    position: 'absolute',
    left: `${(x / VB_W) * 100}%`,
    top: `${(y / VB_H) * 100}%`,
    transform: 'translate(-50%, -50%)',
    borderRadius: 'full',
    pointerEvents: 'none',
  };
  return (
    <>
      {glow && <Box {...at} w="22px" h="22px" bg={color} opacity={0.2} />}
      <Box
        {...at}
        w="11px"
        h="11px"
        border="3px solid"
        borderColor={color}
        bg={`color-mix(in srgb, ${color} 12%, ${chartColor('redesign.bg.surface')})`}
      />
    </>
  );
}

export default function TimeSeriesChart({
  // endGlow rings the last point with a soft halo; endTag is a small node
  // pinned above it (the dashboard's "120,000 under").
  series = [],            // [{ key, values, color, strokeWidth?, dashed?, fill?, endDot?, endGlow?, endTag? }]
  periods = [],
  granularity = 'month',
  height = 120,
  showHoverLabel = true,
  showEndDots = true,
  showZeroLine = true,
  smooth = true,
  // A sparkline inside a clickable row wants none of the pointer affordances;
  // a card-sized chart wants all of them.
  interactive = true,
  // Cap the points actually drawn. A 70px sparkline handed a year of daily
  // balances draws every wiggle and reads as a scribble; scaling to the data
  // range (which the big charts need) amplifies exactly that noise. Sampling
  // down to the shape is what a sparkline is for.
  maxPoints,
  // The number of x positions the axis has. By default every series spans the
  // full width; with `slots`, a series shorter than it stops part-way, as a
  // month to date does against a whole month.
  slots,
  width = '100%',
  my = 4,
  flexShrink,
  onHover,
  ariaLabel,
}) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const containerRef = useRef(null);

  const downsample = (values) => {
    if (!maxPoints || values.length <= maxPoints) return values;
    const step = (values.length - 1) / (maxPoints - 1);
    return Array.from({ length: maxPoints }, (_, i) => values[Math.round(i * step)]);
  };

  const lines = series
    .filter((s) => Array.isArray(s.values) && s.values.length)
    .map((s) => (maxPoints ? { ...s, values: downsample(s.values) } : s));
  const allValues = lines.flatMap((s) => s.values);
  const count = Math.max(...lines.map((s) => s.values.length), slots || 0, 0);
  const xStep = (values) => VB_W / ((slots || values.length) - 1);
  const endX = (s) => (slots ? (s.values.length - 1) * xStep(s.values) : VB_W);

  // Scale to the data, not to zero. A balance oscillating between 28.4M and
  // 28.5M on a 0-anchored axis is a flat line hugging the ceiling above a block
  // of fill — every chart looked inert for that reason. Anchoring to zero is
  // only honest for series that start there, and those reach it on their own.
  const rawMax = Math.max(...allValues);
  const rawMin = Math.min(...allValues);
  const spread = rawMax - rawMin;
  // A dead-flat series has no range to scale to; give it one so it draws
  // through the middle instead of collapsing onto an edge.
  const pad = spread === 0 ? Math.max(Math.abs(rawMax) * 0.1, 1) : 0;
  const max = rawMax + pad;
  const min = rawMin - pad;
  const range = max - min || 1;
  const yFor = (v) => VB_H - (((v - min) / range) * (VB_H * 0.85) + VB_H * 0.075);

  const buildPath = (values) => {
    if (!values.length) return '';
    if (values.length === 1) {
      const y = yFor(values[0]).toFixed(1);
      return `M0,${y} L${VB_W},${y}`;
    }
    const step = xStep(values);
    const pts = values.map((v, i) => ({ x: i * step, y: yFor(v) }));
    // Dense series are already organic; smoothing only earns its keep when the
    // points are sparse enough for the joins to show.
    if (!smooth || pts.length > 120) {
      return pts.map((pt, i) => `${i === 0 ? 'M' : 'L'}${pt.x.toFixed(1)},${pt.y.toFixed(1)}`).join(' ');
    }
    return smoothPath(pts);
  };

  const report = useCallback((idx) => {
    setHoverIdx(idx);
    if (onHover) onHover(idx);
  }, [onHover]);

  const handleMove = useCallback((e) => {
    const el = containerRef.current;
    if (!el || count === 0 || !interactive) return;
    const rect = el.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    report(Math.max(0, Math.min(Math.round(pct * (count - 1)), count - 1)));
  }, [count, report, interactive]);

  const handleLeave = useCallback(() => report(null), [report]);

  const stepX = count > 1 ? VB_W / (count - 1) : 0;
  const hoverX = hoverIdx !== null ? hoverIdx * stepX : null;
  const hoverPeriod = hoverIdx !== null ? periods[hoverIdx] : null;
  const hoverLabel = hoverPeriod ? formatPeriodLabel(hoverPeriod, granularity) : null;

  const baselineY = VB_H;
  const crossesZero = showZeroLine && min < 0 && max > 0;

  return (
    <Box my={my} w={width} flexShrink={flexShrink}>
      {showHoverLabel && interactive && (
        // reserved even when empty, so the chart does not jump on hover
        <Box h="18px" position="relative" mb={1}>
          {hoverLabel && hoverX !== null && (
            <Text
              position="absolute" fontSize="2xs" color="redesign.text.muted"
              fontWeight={500} whiteSpace="nowrap" pointerEvents="none"
              style={{ left: `${(hoverX / VB_W) * 100}%`, transform: 'translateX(-50%)' }}
            >
              {hoverLabel}
            </Text>
          )}
        </Box>
      )}

      <Box
        ref={containerRef}
        position="relative"
        h={`${height}px`}
        cursor={interactive && count > 1 ? 'crosshair' : 'default'}
        onMouseMove={interactive ? handleMove : undefined}
        onMouseLeave={interactive ? handleLeave : undefined}
      >
        {/* overflow visible: the line's round cap at either edge would
            otherwise be cut in half. */}
        <Box as="svg" viewBox={`0 0 ${VB_W} ${VB_H}`} w="100%" h="100%" overflow="visible"
             preserveAspectRatio="none" role="img" aria-label={ariaLabel}>
          <defs>
            {lines.map((s) => s.gradientStops && (
              <linearGradient key={`${s.key}-stroke`} id={`tsc-stroke-${s.key}`} x1="0%" y1="0%" x2="100%" y2="0%">
                {s.gradientStops.map((g, i) => (
                  <stop key={i} offset={g.offset} stopColor={g.color} />
                ))}
              </linearGradient>
            ))}
            {lines.map((s) => s.fill && (
              // Copilot's fill keeps a floor: it fades from 14% to 5% of the
              // line's colour, so the area under the line still reads as one.
              <linearGradient key={s.key} id={`tsc-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={s.color} stopOpacity="0.14" />
                <stop offset="100%" stopColor={s.color} stopOpacity="0.05" />
              </linearGradient>
            ))}
          </defs>

          {lines.map((s) => {
            const d = buildPath(s.values);
            if (!d || !s.fill) return null;
            return <path key={`${s.key}-fill`}
                         d={`${d} L${endX(s)},${baselineY.toFixed(1)} L0,${baselineY.toFixed(1)} Z`}
                         fill={`url(#tsc-${s.key})`} />;
          })}

          {crossesZero && (
            <line x1="0" y1={yFor(0)} x2={VB_W} y2={yFor(0)}
                  stroke={chartColor('redesign.text.faint')} strokeWidth="1" strokeDasharray="3,3" />
          )}

          {lines.map((s) => {
            const d = buildPath(s.values);
            return d && (
              <path key={s.key} d={d} fill="none"
                    stroke={s.gradientStops ? `url(#tsc-stroke-${s.key})` : s.color}
                    strokeWidth={s.strokeWidth ?? 2.5}
                    strokeDasharray={s.dashed ? '4,3' : undefined}
                    strokeLinecap="round" strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke" />
            );
          })}

          {hoverX !== null && (
            <>
              <line x1={hoverX} y1={0} x2={hoverX} y2={VB_H}
                    stroke={chartColor('redesign.text.muted')} strokeWidth="0.8" strokeDasharray="3,3" />
            </>
          )}
        </Box>

        {/* a ring on every series, not just the first */}
        {hoverX !== null && lines.map((s) => {
          const v = s.values[hoverIdx];
          return v === undefined ? null : (
            <PointRing key={`${s.key}-hover`} x={hoverX} y={yFor(v)} color={s.color} />
          );
        })}

        {showEndDots && hoverIdx === null && lines.map((s) => {
          const v = s.values[s.values.length - 1];
          return v === undefined || s.endDot === false ? null : (
            <PointRing key={`${s.key}-end`} x={endX(s)} y={yFor(v)} color={s.color} glow={s.endGlow} />
          );
        })}

        {showEndDots && hoverIdx === null && lines.map((s) => {
          const v = s.values[s.values.length - 1];
          if (!s.endTag || v === undefined) return null;
          const x = endX(s);
          // Centred over the point, unless that would run past the right edge.
          const shiftX = x > VB_W * 0.85 ? '-100%' : '-50%';
          return (
            <Box key={`${s.key}-tag`} position="absolute" pointerEvents="none"
                 left={`${(x / VB_W) * 100}%`} top={`${(yFor(v) / VB_H) * 100}%`}
                 transform={`translate(${shiftX}, calc(-100% - 10px))`}>
              {s.endTag}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
